/**
 * ClientDiscoveryHandler.ts
 * 
 * BATON Handler for device/zone discovery on the Web Client.
 * 
 * This handler communicates with the Control Bridge's DiscoveryHandler
 * to get available audio zones (UPnP, Chromecast devices).
 * 
 * Commands (to server):
 *   - startScan      → Start zone discovery
 *   - stopScan       → Stop zone discovery
 *   - getZones       → Get all discovered zones
 *   - setActiveZone  → Select playback zone
 *   - getActiveZone  → Get current active zone
 *   - refreshZones   → Trigger immediate rescan
 * 
 * Events (from server):
 *   - zonesChanged       → Zone list updated
 *   - activeZoneChanged  → Active zone changed
 *   - scanComplete       → Scan finished
 * 
 * Usage:
 *   import { getClientDiscoveryHandler } from './ClientDiscoveryHandler';
 *   
 *   const handler = getClientDiscoveryHandler();
 *   handler.connect(webSocket);
 *   
 *   const zones = await handler.getZones();
 *   await handler.setActiveZone('upnp_living_room');
 */

import { useMaestroStore } from '../store/maestroStore';
import {
  isConnectorAvailable,
  getRenderers,
  onRenderersUpdated,
  type Renderer,
} from '../lib/connector';
import { isIOSNativeApp, detectPlatform } from '../utils/platform';
import { diagnosticLog } from '../utils/diagnosticLogger';

console.log('[ClientDiscoveryHandler] MODULE LOADED — v2026-03-20-ios-native | isConnector:', isConnectorAvailable(), '| isIOSNative:', isIOSNativeApp());

// =============================================================================
// TYPES
// =============================================================================

export interface Zone {
  id: string;
  name: string;
  type: 'upnp' | 'airplay' | 'chromecast' | 'browser' | 'manual' | 'lua' | 'psaudio' | 'local-native';
  host: string;
  port: number;
  status: 'available' | 'connected' | 'playing' | 'offline';
  capabilities: {
    protocols: string[];
    streaming?: boolean;
    volume?: boolean;
  };
  lastSeen: number;
  metadata?: Record<string, unknown>;
}

// =============================================================================
// RENDERER → ZONE CONVERSION (Tauri IPC ↔ BATON zone model)
// =============================================================================

function makeLocalComputerZone(): Zone {
  return {
    id: 'local_computer',
    name: 'System Audio',
    type: 'browser',
    host: '127.0.0.1',
    port: 0,
    status: 'available',
    capabilities: { protocols: ['*'], streaming: true, volume: true },
    lastSeen: Date.now(),
  };
}

function rendererToZone(r: Renderer): Zone {
  const transport = r.transport_type || 'upnp';

  let zoneType: Zone['type'] = 'upnp';
  if (transport === 'local-native') zoneType = 'local-native';
  else if (transport === 'bluesound') zoneType = 'manual';
  else if (transport === 'raat') zoneType = 'psaudio';
  else if (r.is_roon_ready) zoneType = 'psaudio';

  let port = 0;
  try {
    if (r.control_url) {
      const parsed = new URL(r.control_url);
      if (parsed.port) port = parseInt(parsed.port, 10);
    }
  } catch { /* invalid URL — ignore */ }

  return {
    id: r.id,
    name: r.custom_name || r.name,
    type: zoneType,
    host: r.ip || '',
    port,
    status: 'available',
    capabilities: {
      protocols: r.sink_protocols || [],
      streaming: r.can_play !== false || !!r.raat_port,
      volume: !!(r.volume_url || r.raat_port),
      fixedVolume: !!r.fixed_volume,
    },
    lastSeen: Date.now(),
    metadata: {
      source: 'native',
      model: r.model,
      manufacturer: r.manufacturer,
      transport_type: transport,
      is_roon_ready: r.is_roon_ready || false,
      icon_url: r.icon_url || '',
      uses_stop_for_pause: r.uses_stop_for_pause || false,
      control_url: r.control_url || '',
      volume_url: r.volume_url || '',
      raat_port: r.raat_port || null,
    },
  };
}

function renderersToZones(renderers: Renderer[]): Zone[] {
  return [makeLocalComputerZone(), ...renderers.map(rendererToZone)];
}

export interface DiscoveryEvent {
  event: string;
  zones?: Zone[];
  activeZone?: Zone;
  timestamp?: number;
}

export interface DiscoveryResponse {
  success: boolean;
  zones?: Zone[];
  activeZone?: Zone;
  message?: string;
  error?: string;
}

type DiscoveryCommandPromise = {
  resolve: (value: DiscoveryResponse) => void;
  reject: (reason: Error) => void;
  timeout: NodeJS.Timeout;
};

// =============================================================================
// CLIENT DISCOVERY HANDLER
// =============================================================================

export class ClientDiscoveryHandler {
  readonly handlerId = 'discovery';
  readonly location = 'bridge';
  private static readonly CONNECTOR_ZONE_STORAGE_KEY = 'maestro_connector_active_zone';
  private static readonly ZONE_TYPE_PRIORITY: Record<string, number> = {
    browser: 100,
    upnp: 80,
    psaudio: 70,
    lua: 60,
    chromecast: 50,
    airplay: 40,
    manual: 10,
  };
  
  private ws: WebSocket | null = null;
  private pendingCommands: Map<string, DiscoveryCommandPromise> = new Map();
  private localZonesCache: Zone[] = [];
  private activeZone: Zone | null = null;
  
  // Tauri event unsubscribe handle (Connector mode only)
  private connectorUnlisten: (() => void) | null = null;
  
  // Callbacks for UI updates
  private onZonesChangedCallbacks: Set<(zones: Zone[]) => void> = new Set();
  private onActiveZoneChangedCallbacks: Set<(zone: Zone | null) => void> = new Set();
  
  private connectorInitialized = false;
  private iosNativeInitialized = false;
  private tauriPollTimer: ReturnType<typeof setInterval> | null = null;
  private rendererPollTimer: ReturnType<typeof setInterval> | null = null;
  private rendererPollCount = 0;

  private _isLocalZone(zone: Zone): boolean {
    return zone.type === 'browser'
      || zone.id === 'local_computer'
      || zone.id === 'this_computer'
      || zone.id === 'this_device';
  }

  /**
   * Sync both store.setActiveZone AND store.setActiveConnectorRenderer together.
   * TransportCommand reads activeConnectorRenderer — if we only set activeZone,
   * transport commands route to nothing.
   */
  private _updateStoreZone(zone: Zone | null) {
    const store = useMaestroStore.getState();
    const current = store.activeConnectorRenderer;
    const isRemote = zone && !this._isLocalZone(zone);

    if (isRemote) {
      if (current?.id === zone.id) return;
      store.setActiveZone(zone);
      store.setActiveConnectorRenderer({ id: zone.id, name: zone.name });
      console.log('[ClientDiscoveryHandler] setActiveZone + setActiveConnectorRenderer:', zone.name, zone.id);
    } else {
      if (!current) return;
      store.setActiveZone(zone);
      store.setActiveConnectorRenderer(null);
    }
  }

  constructor() {
    console.log('[ClientDiscoveryHandler] Initialized');
    this.waitForNativeAndStart();
  }

  /**
   * Poll for __TAURI__ or __maestroNative availability and start discovery.
   * Both Tauri and the iOS native bridge inject asynchronously — may not exist
   * when the module first loads, so we poll every 200ms for up to 10s.
   */
  private waitForNativeAndStart(): void {
    if (isConnectorAvailable()) {
      this.startConnectorDiscovery();
      return;
    }
    if (isIOSNativeApp()) {
      this.startIOSNativeDiscovery();
      return;
    }
    let attempts = 0;
    this.tauriPollTimer = setInterval(() => {
      attempts++;
      if (isConnectorAvailable()) {
        if (this.tauriPollTimer) { clearInterval(this.tauriPollTimer); this.tauriPollTimer = null; }
        this.startConnectorDiscovery();
        return;
      }
      if (isIOSNativeApp()) {
        if (this.tauriPollTimer) { clearInterval(this.tauriPollTimer); this.tauriPollTimer = null; }
        this.startIOSNativeDiscovery();
        return;
      }
      if (attempts >= 50) {
        if (this.tauriPollTimer) { clearInterval(this.tauriPollTimer); this.tauriPollTimer = null; }
      }
    }, 200);
  }

  /**
   * Start Connector discovery — subscribe to renderer events and fetch zones.
   * Called once __TAURI__ is confirmed available.
   */
  private startConnectorDiscovery(): void {
    if (this.connectorInitialized) return;
    this.connectorInitialized = true;
    console.log('[ClientDiscoveryHandler] __TAURI__ ready — starting Connector discovery NOW');

    onRenderersUpdated((renderers: Renderer[]) => {
      console.log(`[ClientDiscoveryHandler] 🔄 Rust renderer update: ${renderers.length} devices`);
      const zones = renderersToZones(renderers);
      this.applyZones(zones);
      // The initial restoreConnectorZone runs before renderers arrive (0 results).
      // Re-attempt here so the saved zone is set once renderers are discovered.
      if (!this.activeZone || this.activeZone.id === 'local_computer') {
        this.restoreConnectorZone(zones).catch(() => {});
      }
    }).then((unlisten) => {
      this.connectorUnlisten = unlisten;
      console.log('[ClientDiscoveryHandler] ✅ Event subscription established');
    }).catch((err) => {
      console.warn('[ClientDiscoveryHandler] Failed to subscribe to renderer updates:', err);
    });

    this.getZones().then(response => {
      if (response.success && response.zones) {
        console.log(`[ClientDiscoveryHandler] ✅ Initial fetch: ${response.zones.length} zones`);
        this.applyZones(response.zones);
        this.startScan().then((scanResponse) => {
          if (scanResponse.success && scanResponse.zones) {
            console.log(`[ClientDiscoveryHandler] Scan complete: ${scanResponse.zones.length} zones`);
            this.applyZones(scanResponse.zones);
          }
          this.restoreConnectorZone(scanResponse.zones || response.zones || []).catch(() => {});
        }).catch(() => {
          this.restoreConnectorZone(response.zones || []).catch(() => {});
        });
      }
    }).catch(err => {
      console.error('[ClientDiscoveryHandler] Initial fetch failed:', err);
    });

    this.startRendererPolling();
  }

  // ---------------------------------------------------------------------------
  // iOS NATIVE DISCOVERY (parallel to Connector discovery)
  // ---------------------------------------------------------------------------

  /**
   * Start iOS native discovery — listen for CustomEvents from the Swift bridge
   * and request the initial renderer list. Called once __maestroNative is confirmed.
   */
  private startIOSNativeDiscovery(): void {
    if (this.iosNativeInitialized) return;
    this.iosNativeInitialized = true;
    console.log('[ClientDiscoveryHandler] 📲 __maestroNative ready — starting iOS native discovery');

    window.addEventListener('maestroRenderers', ((event: CustomEvent) => {
      const renderers: Renderer[] = event.detail;
      console.log(`[ClientDiscoveryHandler] 📲 iOS renderer update: ${renderers?.length ?? 0} devices`);
      if (Array.isArray(renderers)) {
        const zones = renderersToZones(renderers);
        this.applyZones(zones);
        if (!this.activeZone || this.activeZone.id === 'local_computer') {
          this.restoreIOSNativeZone(zones).catch(() => {});
        }
      }
    }) as EventListener);

    window.addEventListener('maestroVolumeChanged', ((event: CustomEvent) => {
      const { deviceId, volume } = event.detail || {};
      if (deviceId !== undefined && volume !== undefined) {
        const { setVolume } = useMaestroStore.getState();
        setVolume(volume);
      }
    }) as EventListener);

    // Request initial renderer list and trigger a scan
    const native = (window as any).__maestroNative;
    if (native?.getRenderers) {
      native.getRenderers();
    }
    if (native?.startDiscovery) {
      native.startDiscovery();
    }

    // Poll for renderers to catch late-arriving devices
    this.startIOSRendererPolling();
  }

  /**
   * Poll for renderers on iOS every 3s for 30s. Native SSDP/RAAT discovery
   * runs in the background and devices appear incrementally.
   */
  private startIOSRendererPolling(): void {
    let pollCount = 0;
    const timer = setInterval(() => {
      // During playback, maestroRenderers → applyZones preserves the active zone row (see
      // preserveActiveZoneDuringPlayback). No transport calls from discovery.
      pollCount++;
      const native = (window as any).__maestroNative;
      if (native?.getRenderers) {
        native.getRenderers();
      }
      if (pollCount >= 10) {
        clearInterval(timer);
        console.log('[ClientDiscoveryHandler] 📲 iOS renderer polling complete (30s)');
      }
    }, 3000);
  }

  /**
   * Handle discovery commands locally when running inside the iOS native app.
   * The iPad IS the discovery source — no cloud round-trip needed.
   */
  private async handleIOSNativeCommand(
    cmd: string,
    payload: Record<string, unknown>,
  ): Promise<DiscoveryResponse> {
    switch (cmd) {
      case 'getZones':
      case 'startScan':
      case 'refreshZones': {
        const native = (window as any).__maestroNative;
        if (native?.getRenderers) {
          native.getRenderers();
        }
        // Return current cached zones (real data arrives via event)
        return { success: true, zones: this.localZonesCache.length > 0 ? this.localZonesCache : renderersToZones([]) };
      }

      case 'stopScan':
        return { success: true };

      case 'setActiveZone': {
        const zoneId = payload.zoneId as string;
        const zone = this.localZonesCache.find((z) => z.id === zoneId) || null;
        if (zone) {
          this.activeZone = zone;
          this.notifyActiveZoneChanged(zone);
          this.saveIOSNativeZoneId(zone.id);
          this._updateStoreZone(zone);
        }
        return { success: true, activeZone: zone || undefined };
      }

      case 'getActiveZone':
        return { success: true, activeZone: this.activeZone || undefined };

      default:
        return { success: false, error: `Unknown command: ${cmd}` };
    }
  }

  private static readonly IOS_ZONE_STORAGE_KEY = 'maestro_ios_active_zone';

  private getSavedIOSNativeZoneId(): string | null {
    try {
      return localStorage.getItem(ClientDiscoveryHandler.IOS_ZONE_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private saveIOSNativeZoneId(zoneId: string): void {
    try {
      localStorage.setItem(ClientDiscoveryHandler.IOS_ZONE_STORAGE_KEY, zoneId);
    } catch { /* ignore */ }
  }

  private async restoreIOSNativeZone(zones: Zone[]): Promise<void> {
    const store = useMaestroStore.getState();
    if (store.activeConnectorRenderer) {
      const syncedId = store.activeConnectorRenderer.id;
      const match = zones.find((z) => z.id === syncedId);
      if (match) {
        console.log(`[ClientDiscoveryHandler] 📲 Adopting synced zone: ${match.name}`);
        this.activeZone = match;
        this.notifyActiveZoneChanged(match);
        return;
      }
    }

    const savedZoneId = this.getSavedIOSNativeZoneId();
    if (savedZoneId) {
      const saved = zones.find((z) => z.id === savedZoneId);
      if (saved) {
        console.log(`[ClientDiscoveryHandler] 📲 Restoring saved iOS zone: ${saved.name}`);
        this.activeZone = saved;
        this.notifyActiveZoneChanged(saved);
        this._updateStoreZone(saved);
        return;
      }
    }
  }

  /**
   * Poll getRenderers() every 3s for 30s. The initial IPC call often returns 0
   * renderers because Rust UPnP/SSDP discovery runs in the background and takes
   * several seconds. This polling catches those late arrivals even if the event
   * subscription failed due to WebKit's ipc:// mixed-content block.
   */
  private lastRendererIds: string = '';

  private startRendererPolling(): void {
    this.rendererPollCount = 0;
    if (this.rendererPollTimer) { clearInterval(this.rendererPollTimer); }
    this.rendererPollTimer = setInterval(async () => {
      this.rendererPollCount++;
      try {
        const renderers = await getRenderers();
        if (renderers.length > 0) {
          const idKey = renderers.map(r => r.id).sort().join(',');
          const zonesChanged = idKey !== this.lastRendererIds;
          if (zonesChanged) {
            this.lastRendererIds = idKey;
            console.log(`[ClientDiscoveryHandler] Poll #${this.rendererPollCount}: ${renderers.length} renderers found`);
            const zones = renderersToZones(renderers);
            this.applyZones(zones);
          }
          if (!this.activeZone || this.activeZone.id === 'local_computer') {
            const zones = zonesChanged ? this.localZonesCache : renderersToZones(renderers);
            this.restoreConnectorZone(zones).catch(() => {});
          }
        }
      } catch { /* IPC not ready yet */ }
      if (this.rendererPollCount >= 10) {
        if (this.rendererPollTimer) { clearInterval(this.rendererPollTimer); this.rendererPollTimer = null; }
        console.log('[ClientDiscoveryHandler] Renderer polling complete (30s)');
      }
    }, 3000);
  }
  
  // ---------------------------------------------------------------------------
  // CONNECTION MANAGEMENT
  // ---------------------------------------------------------------------------
  
  /**
   * Connect to WebSocket (shares connection with ClientTransportHandler)
   * On iOS native, discovery works via native bridge without WebSocket
   */
  connect(ws: WebSocket | null): void {
    this.ws = ws;
    
    if (ws) {
      console.log('[ClientDiscoveryHandler] Connected to WebSocket');
      console.log('[ClientDiscoveryHandler] WebSocket readyState:', ws.readyState);
    }
    
    const isConnector = isConnectorAvailable();
    const isIOS = isIOSNativeApp();

    // iOS native: start discovery if constructor missed it, otherwise refresh.
    if (isIOS) {
      if (!this.iosNativeInitialized) {
        console.log('[ClientDiscoveryHandler] 📲 iOS native on connect() — starting discovery');
        this.startIOSNativeDiscovery();
      } else {
        console.log('[ClientDiscoveryHandler] 📲 iOS native already initialized — refreshing');
        const native = (window as any).__maestroNative;
        if (native?.getRenderers) native.getRenderers();
      }
      return;
    }

    // Connector: start discovery if constructor missed it, otherwise refresh.
    if (isConnector) {
      if (!this.connectorInitialized) {
        console.log('[ClientDiscoveryHandler] Connector available on connect() — starting discovery now');
        this.startConnectorDiscovery();
      } else {
        console.log('[ClientDiscoveryHandler] Connector already initialized — refreshing zones');
        this.getZones().then(r => {
          if (r.success && r.zones) this.applyZones(r.zones);
        }).catch(() => {});
      }
      return;
    }

    // Non-Connector path: iOS native needs a longer delay for discovery.
    const delay = 1000;
    
    setTimeout(() => {
      console.log('[ClientDiscoveryHandler] Fetching initial zones...');
      this.getZones().then(response => {
        if (response.success && response.zones) {
          console.log(`[ClientDiscoveryHandler] ✅ Got ${response.zones.length} zones`);
          this.applyZones(response.zones);

          if (response.zones.length === 0) {
            console.log('[ClientDiscoveryHandler] No zones yet; starting scan…');
            this.startScan()
              .then(() => {
                setTimeout(() => {
                  this.getZones()
                    .then((r2) => {
                      if (r2.success && r2.zones) {
                        this.applyZones(r2.zones);
                        console.log(`[ClientDiscoveryHandler] Post-scan zones: ${r2.zones.length}`);
                      }
                    })
                    .catch(() => {});
                }, 2500);
              })
              .catch((err) => {
                console.warn('[ClientDiscoveryHandler] startScan failed:', err);
              });
          }

          if (isConnector) {
            this.startScan()
              .then((scanResponse) => {
                const zones = scanResponse.zones || response.zones || [];
                if (scanResponse.success && scanResponse.zones) {
                  console.log(`[ClientDiscoveryHandler] Connector scan complete — ${scanResponse.zones.length} zones`);
                  this.applyZones(scanResponse.zones);
                } else {
                  console.log(`[ClientDiscoveryHandler] startScan returned no zones, using initial ${zones.length}`);
                }
                return this.restoreConnectorZone(zones);
              })
              .catch((err) => {
                console.warn('[ClientDiscoveryHandler] Connector scan failed:', err);
                this.restoreConnectorZone(response.zones || []).catch(() => {});
              });
            return;
          }
        } else {
          console.error('[ClientDiscoveryHandler] getZones FULL:', JSON.stringify(response, null, 2));
        }
      }).catch(err => {
        console.error('[ClientDiscoveryHandler] Failed to fetch initial zones:', err);
      });
    }, delay);
  }
  
  /**
   * Disconnect from WebSocket
   */
  disconnect(): void {
    this.ws = null;
    this.pendingCommands.forEach(promise => {
      clearTimeout(promise.timeout);
      promise.reject(new Error('Disconnected'));
    });
    this.pendingCommands.clear();

    // In Connector mode, keep the Tauri event subscription alive across
    // WebSocket reconnects — the Connector discovery is independent of WS.
    if (!isConnectorAvailable() && this.connectorUnlisten) {
      this.connectorUnlisten();
      this.connectorUnlisten = null;
    }

    console.log('[ClientDiscoveryHandler] Disconnected');
  }
  
  // ---------------------------------------------------------------------------
  // PUBLIC API - Commands
  // ---------------------------------------------------------------------------
  
  /**
   * Start zone discovery
   */
  async startScan(): Promise<DiscoveryResponse> {
    return this.sendCommand('startScan', {});
  }
  
  /**
   * Stop zone discovery
   */
  async stopScan(): Promise<DiscoveryResponse> {
    return this.sendCommand('stopScan', {});
  }
  
  /**
   * Get all discovered zones
   */
  async getZones(): Promise<DiscoveryResponse> {
    const response = await this.sendCommand('getZones', {});
    if (response.success && response.zones) {
      this.localZonesCache = response.zones;
      try {
        const { setDiscoveredZones } = useMaestroStore.getState();
        setDiscoveredZones(response.zones);
      } catch {
        // ignore
      }
    }
    return response;
  }
  
  /**
   * Set the active playback zone
   */
  async setActiveZone(zoneId: string): Promise<DiscoveryResponse> {
    const preferredZone = this.getPreferredZone(zoneId, this.localZonesCache);
    const targetZoneId = preferredZone?.id || zoneId;
    const response = await this.sendCommand('setActiveZone', { zoneId: targetZoneId });
    if (response.success && response.activeZone) {
      const canonicalActiveZone = this.getPreferredZone(response.activeZone, this.localZonesCache) || response.activeZone;
      this.activeZone = canonicalActiveZone;
      this.notifyActiveZoneChanged(canonicalActiveZone);
      this.saveConnectorZoneId(canonicalActiveZone.id);
      
      this._updateStoreZone(canonicalActiveZone);

      const shouldUpgradeConnectorZone = isConnectorAvailable()
        && response.activeZone.type === 'airplay'
        && canonicalActiveZone.id !== response.activeZone.id;
      if (shouldUpgradeConnectorZone) {
        console.log(`[ClientDiscoveryHandler] Upgrading connector zone from ${response.activeZone.id} to ${canonicalActiveZone.id}`);
        return this.setActiveZone(canonicalActiveZone.id);
      }
    }
    return response;
  }
  
  /**
   * Get the current active zone
   */
  async getActiveZone(): Promise<DiscoveryResponse> {
    const response = await this.sendCommand('getActiveZone', {});
    if (response.success && response.activeZone) {
      this.activeZone = response.activeZone;
    }
    return response;
  }
  
  /**
   * Trigger immediate zone refresh
   */
  async refreshZones(): Promise<DiscoveryResponse> {
    const response = await this.sendCommand('refreshZones', {});
    if (response.success && response.zones) {
      this.localZonesCache = response.zones;
      this.notifyZonesChanged(response.zones);
      try {
        const { setDiscoveredZones } = useMaestroStore.getState();
        setDiscoveredZones(response.zones);
      } catch {
        // ignore
      }
    }
    return response;
  }
  
  /**
   * Get cached zones (synchronous)
   */
  getCachedZones(): Zone[] {
    return [...this.localZonesCache];
  }
  
  /**
   * Get cached active zone (synchronous)
   */
  getCachedActiveZone(): Zone | null {
    return this.activeZone;
  }
  
  // ---------------------------------------------------------------------------
  // EVENT CALLBACKS
  // ---------------------------------------------------------------------------
  
  /**
   * Subscribe to zones changed events
   */
  onZonesChanged(callback: (zones: Zone[]) => void): () => void {
    this.onZonesChangedCallbacks.add(callback);
    return () => this.onZonesChangedCallbacks.delete(callback);
  }
  
  /**
   * Subscribe to active zone changed events
   */
  onActiveZoneChanged(callback: (zone: Zone | null) => void): () => void {
    this.onActiveZoneChangedCallbacks.add(callback);
    return () => this.onActiveZoneChangedCallbacks.delete(callback);
  }
  
  // ---------------------------------------------------------------------------
  // EVENT HANDLING (from server)
  // ---------------------------------------------------------------------------
  
  /**
   * Handle incoming BATON events
   */
  handleEvent(event: DiscoveryEvent): void {
    const eventType = event.event;
    console.log(`[ClientDiscoveryHandler] Event received: ${eventType}`);
    
    switch (eventType) {
      case 'zonesChanged':
        if (event.zones) {
          this.applyZones(event.zones);
          this.maybeUpgradeActiveConnectorZone(event.zones);
        }
        break;
        
      case 'activeZoneChanged':
        if (event.activeZone) {
          this.activeZone = event.activeZone;
          this.notifyActiveZoneChanged(event.activeZone);
          this.saveConnectorZoneId(event.activeZone.id);
          this._updateStoreZone(event.activeZone);
        }
        break;
        
      case 'scanComplete':
        console.log('[ClientDiscoveryHandler] Scan complete');
        if (event.zones) {
          this.applyZones(event.zones);
          this.maybeUpgradeActiveConnectorZone(event.zones);
        }
        break;
        
      case 'volumeChanged':
        // Volume was changed by a network renderer command on the server
        // Update the local store to reflect the new volume
        console.log(`[ClientDiscoveryHandler] 🔊 Volume changed: ${(event as any).level}% via ${(event as any).method}`);
        if ((event as any).level !== undefined) {
          const { setVolume, setMuted } = useMaestroStore.getState();
          setVolume((event as any).level);
          if ((event as any).muted !== undefined) {
            setMuted((event as any).muted);
          }
        }
        break;
        
      case 'rendererStateChanged':
        // Renderer playback state changed (playing, paused, stopped)
        console.log(`[ClientDiscoveryHandler] 🎵 Renderer state: ${(event as any).state} on ${(event as any).zoneName}`);
        // Could update playback state in store here if needed
        break;
        
      default:
        console.warn(`[ClientDiscoveryHandler] Unknown event: ${eventType}`);
    }
  }
  
  /**
   * Handle BATON response
   */
  handleResponse(message: { id: string; payload: DiscoveryResponse }): void {
    const id = message.id;
    console.log(`[ClientDiscoveryHandler] 📥 Response received for id: ${id}`);
    console.log('[ClientDiscoveryHandler] Pending commands:', Array.from(this.pendingCommands.keys()));
    
    const pending = this.pendingCommands.get(id);
    
    if (pending) {
      console.log('[ClientDiscoveryHandler] ✅ Found pending command, resolving...');
      clearTimeout(pending.timeout);
      this.pendingCommands.delete(id);
      pending.resolve(message.payload);
    } else {
      console.warn('[ClientDiscoveryHandler] ⚠️ No pending command found for id:', id);
    }
  }
  
  // ---------------------------------------------------------------------------
  // INTERNAL - Connector-Local Command Handling (Tauri IPC)
  // ---------------------------------------------------------------------------

  /**
   * Handle discovery commands locally via Tauri IPC when running inside
   * the Connector. This is the primary path — no cloud round-trip.
   */
  private async handleConnectorCommand(
    cmd: string,
    payload: Record<string, unknown>,
  ): Promise<DiscoveryResponse> {
    switch (cmd) {
      case 'getZones':
      case 'startScan':
      case 'refreshZones': {
        try {
          const renderers = await getRenderers();
          const zones = renderersToZones(renderers);
          console.log(`[ClientDiscoveryHandler] Connector IPC: ${renderers.length} renderers → ${zones.length} zones`);
          return { success: true, zones };
        } catch (err) {
          console.error('[ClientDiscoveryHandler] Tauri getRenderers failed:', err);
          return { success: false, error: String(err) };
        }
      }

      case 'stopScan':
        return { success: true };

      case 'setActiveZone': {
        const zoneId = payload.zoneId as string;
        const zone = this.localZonesCache.find((z) => z.id === zoneId) || null;
        if (zone) {
          this.activeZone = zone;
          this.notifyActiveZoneChanged(zone);
          this.saveConnectorZoneId(zone.id);
          this._updateStoreZone(zone);
        }
        return { success: true, activeZone: zone || undefined };
      }

      case 'getActiveZone':
        return { success: true, activeZone: this.activeZone || undefined };

      default:
        console.warn(`[ClientDiscoveryHandler] Unknown Connector command: ${cmd}`);
        return { success: false, error: `Unknown command: ${cmd}` };
    }
  }
  
  // ---------------------------------------------------------------------------
  // INTERNAL - iOS Native Bridge Check
  // ---------------------------------------------------------------------------
  
  private getIOSBridge(): any {
    if (!detectPlatform().isIOSNativeApp) return null;
    return (window as any).__maestroNative
      || (window as any).webkit?.messageHandlers?.baton
      || (window as any).__MAESTRO_BRIDGE__;
  }
  
  private isNativeiOS(): boolean {
    return detectPlatform().isIOSNativeApp;
  }

  private getSavedConnectorZoneId(): string | null {
    if (!isConnectorAvailable()) return null;
    try {
      return localStorage.getItem(ClientDiscoveryHandler.CONNECTOR_ZONE_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private saveConnectorZoneId(zoneId: string): void {
    if (!isConnectorAvailable() || !zoneId) return;
    try {
      localStorage.setItem(ClientDiscoveryHandler.CONNECTOR_ZONE_STORAGE_KEY, zoneId);
    } catch {
      // ignore
    }
  }

  /**
   * Dedup zones discovered via multiple protocols at the SAME IP address.
   * Only merges when two zones share an identical, valid IP — different
   * IPs are always treated as separate physical devices, even if names match.
   * When merging, the zone with the richest protocol wins (RAAT > UPnP).
   */
  private deduplicateZones(zones: Zone[]): Zone[] {
    const PROTOCOL_RANK: Record<string, number> = {
      psaudio: 100, lua: 90, upnp: 70, manual: 60,
      chromecast: 50, airplay: 40, browser: 30,
    };

    const ipGroups = new Map<string, Zone[]>();
    const passThrough: Zone[] = [];

    for (const zone of zones) {
      if (zone.type === 'browser' || zone.id === 'local_computer') {
        passThrough.push(zone);
        continue;
      }

      const ip = zone.host?.trim();
      const ipValid = ip && ip.length > 0 && ip !== '127.0.0.1'
        && ip !== '0.0.0.0' && ip !== 'undefined' && ip !== 'null';

      if (ipValid) {
        if (!ipGroups.has(ip!)) ipGroups.set(ip!, []);
        ipGroups.get(ip!)!.push(zone);
      } else {
        passThrough.push(zone);
      }
    }

    const result: Zone[] = [...passThrough];
    for (const [ip, group] of ipGroups) {
      if (group.length === 1) {
        result.push(group[0]);
        continue;
      }
      group.sort((a, b) =>
        (PROTOCOL_RANK[b.type] ?? 0) - (PROTOCOL_RANK[a.type] ?? 0));
      const winner = group[0];
      const dropped = group.slice(1).map(z => `${z.name}(${z.type})`).join(', ');
      console.log(`[ClientDiscoveryHandler] Dedup @${ip}: keeping ${winner.name} (${winner.type}), dropped: ${dropped}`);
      result.push(winner);
    }

    return result;
  }

  /**
   * While a network renderer is in use, discovery may briefly omit that row (dedupe/IP merge)
   * or native may reshuffle the list. Re-attach the prior row so the picker never drops the
   * active device. Uses renderer/selection only — not playbackState (discovery must not track transport).
   */
  private preserveActiveZoneDuringPlayback(deduped: Zone[]): Zone[] {
    const store = useMaestroStore.getState();
    const rid = store.activeConnectorRenderer?.id || '';
    const remoteRendererSelected =
      !!rid
      && rid !== 'local_computer'
      && rid !== 'this_computer'
      && rid !== 'this_device';
    const hasNetworkTarget = store.rendererHasStream || remoteRendererSelected;
    if (!hasNetworkTarget) return deduped;

    const activeId = store.activeConnectorRenderer?.id || store.discovery?.activeZone?.id;
    if (!activeId) return deduped;
    if (activeId === 'local_computer' || activeId === 'this_computer' || activeId === 'this_device') {
      return deduped;
    }

    if (deduped.some((z) => z.id === activeId)) return deduped;

    const prev = this.localZonesCache.find((z) => z.id === activeId);
    if (!prev) return deduped;

    console.log(
      '[ClientDiscoveryHandler] Playback active — preserving active zone row missing from latest discovery:',
      activeId,
    );
    return [...deduped, prev];
  }

  /**
   * applyZones is discovery-only: merge/dedupe the list and write `discoveredZones`.
   * Never call TransportCommand (pause/play/stop/playUri), never set playbackState,
   * never switch zones — those belong to user gestures / ZoneController / sync only.
   */
  private applyZones(zones: Zone[]): void {
    const stateBefore = useMaestroStore.getState().playbackState;
    try {
      const deduped = this.deduplicateZones(zones);
      const merged = this.preserveActiveZoneDuringPlayback(deduped);
      const beforeList = this.localZonesCache || [];
      const beforeIds = new Set(beforeList.map((z) => z.id));
      const afterIds = new Set(merged.map((z) => z.id));
      for (const z of merged) {
        if (!beforeIds.has(z.id)) {
          diagnosticLog('info', 'discovery', `Device found: ${z.name} (${z.type})`, {
            name: z.name,
            type: z.type,
            ip: z.host,
            protocol: z.type,
          });
        }
      }
      for (const z of beforeList) {
        if (!afterIds.has(z.id)) {
          diagnosticLog('warn', 'discovery', `Device lost: ${z.name}`, {
            name: z.name,
            type: z.type,
            ip: z.host,
            lastSeen: Date.now(),
          });
        }
      }
      console.log(`[ClientDiscoveryHandler] applyZones: ${zones.length} raw → ${deduped.length} after dedup → ${merged.length} merged`);
      diagnosticLog('info', 'discovery', `Renderer list updated: ${merged.length} zones`, { count: merged.length, names: merged.map(z => z.name || z.id) });
      this.localZonesCache = merged;
      this.notifyZonesChanged(merged);
      const storeState = useMaestroStore.getState();
      storeState.setDiscoveredZones(merged);

      // Reconcile activeZone capabilities when the renderer list refreshes.
      // Capabilities like `fixedVolume` (AirLens) or updated volume capability
      // can arrive AFTER the user has already selected a zone — without this,
      // the MiniPlayer never learns the device has fixed volume and keeps
      // showing the slider. Also heals any partial-zone shim written by the
      // sync handler during a remote state adoption.
      const activeId = storeState.discovery?.activeZone?.id;
      if (activeId && activeId !== 'local_computer' && activeId !== 'this_computer' && activeId !== 'this_device') {
        const fresh = merged.find((z) => z.id === activeId);
        const current = storeState.discovery.activeZone;
        if (fresh && current) {
          const freshFixed = !!fresh.capabilities?.fixedVolume;
          const currentFixed = !!current.capabilities?.fixedVolume;
          const freshProtocols = JSON.stringify(fresh.capabilities?.protocols || []);
          const currentProtocols = JSON.stringify(current.capabilities?.protocols || []);
          if (freshFixed !== currentFixed || freshProtocols !== currentProtocols) {
            console.log('[ClientDiscoveryHandler] Reconciling activeZone capabilities:', {
              id: activeId,
              fixedVolume: { was: currentFixed, now: freshFixed },
            });
            const protos = fresh.capabilities?.protocols || [];
            const summary = `${protos.join(',') || 'unknown'} fixedVol=${freshFixed}`;
            diagnosticLog('info', 'capability', `Capabilities for ${fresh.name || activeId}: ${summary}`, {
              name: fresh.name,
              protocols: protos,
              maxSampleRate: (fresh as any).max_sample_rate ?? (fresh as any).maxSampleRate,
              maxBitDepth: (fresh as any).max_bit_depth ?? (fresh as any).maxBitDepth,
            }, `${fresh.ip || ''}|${fresh.name || ''}|${fresh.manufacturer || ''}`);
            storeState.setActiveZone(fresh);
          }
        }
      }
    } finally {
      // Never let zone updates change transport state
      if (useMaestroStore.getState().playbackState !== stateBefore) {
        useMaestroStore.setState({ playbackState: stateBefore });
      }
    }
  }

  private getPreferredZone(zoneOrId: Zone | string | null | undefined, zones: Zone[]): Zone | null {
    if (!zoneOrId || !zones.length) return null;
    const selected = typeof zoneOrId === 'string'
      ? zones.find((zone) => zone.id === zoneOrId) || null
      : zoneOrId;

    if (!selected) return null;

    let best = selected;
    for (const candidate of zones) {
      const sameHost = selected.host && candidate.host && candidate.host === selected.host;
      const sameName = selected.name?.toLowerCase() === candidate.name?.toLowerCase();
      const exactId = candidate.id === selected.id;
      if (!sameHost && !sameName && !exactId) continue;

      const candidatePriority = ClientDiscoveryHandler.ZONE_TYPE_PRIORITY[candidate.type] ?? 0;
      const bestPriority = ClientDiscoveryHandler.ZONE_TYPE_PRIORITY[best.type] ?? 0;
      if (candidatePriority > bestPriority) {
        best = candidate;
      }
    }

    return best;
  }

  private maybeUpgradeActiveConnectorZone(zones: Zone[]): void {
    const ps = useMaestroStore.getState().playbackState;
    if (ps === 'playing' || ps === 'loading') return;
    if (!isConnectorAvailable() || !this.activeZone) return;
    if (this.activeZone.type !== 'airplay') return;

    const preferredZone = this.getPreferredZone(this.activeZone, zones);
    if (!preferredZone || preferredZone.id === this.activeZone.id) return;

    console.log(`[ClientDiscoveryHandler] Auto-upgrading active connector zone to ${preferredZone.name} (${preferredZone.type})`);
    this.setActiveZone(preferredZone.id).catch((err) => {
      console.warn('[ClientDiscoveryHandler] Failed to auto-upgrade active zone:', err);
    });
  }

  private async restoreConnectorZone(zones: Zone[]): Promise<void> {
    const savedZoneId = this.getSavedConnectorZoneId();
    if (savedZoneId) {
      if (this.activeZone && this.activeZone.id === savedZoneId) return;
      const savedZone = this.getPreferredZone(savedZoneId, zones);
      if (savedZone) {
        if (this.activeZone && this.activeZone.id === savedZone.id) return;
        console.log(`[ClientDiscoveryHandler] Restoring saved connector zone: ${savedZone.name} (${savedZone.type})`);
        await this.setActiveZone(savedZone.id);
        return;
      }
    }

    const activeResponse = await this.getActiveZone();
    if (activeResponse.success && activeResponse.activeZone) {
      const restoredZone = activeResponse.activeZone;
      const isUnsupportedAirplay = restoredZone.type === 'airplay';
      if (isUnsupportedAirplay) {
        const localZone = zones.find((zone) => zone.id === 'local_computer');
        if (localZone) {
          console.log('[ClientDiscoveryHandler] Restored airplay zone is unsupported; falling back to System Audio');
          await this.setActiveZone(localZone.id);
          return;
        }
      }

      const preferredRestoredZone = this.getPreferredZone(restoredZone, zones) || restoredZone;
      console.log(`[ClientDiscoveryHandler] ✅ Restored active zone: ${preferredRestoredZone.name}`);
      this._updateStoreZone(preferredRestoredZone);
      this.saveConnectorZoneId(preferredRestoredZone.id);
      if (preferredRestoredZone.id !== restoredZone.id) {
        await this.setActiveZone(preferredRestoredZone.id);
      }
    }
  }
  
  // ---------------------------------------------------------------------------
  // INTERNAL - Command Sending (WebSocket or Native Bridge)
  // ---------------------------------------------------------------------------
  
  private async sendCommand(cmd: string, payload: Record<string, unknown>): Promise<DiscoveryResponse> {
    // Connector-local path: use Tauri IPC directly.
    if (isConnectorAvailable()) {
      return this.handleConnectorCommand(cmd, payload);
    }

    // iOS native path: the iPad IS the discovery source.
    if (isIOSNativeApp()) {
      return this.handleIOSNativeCommand(cmd, payload);
    }

    const iosBridge = this.getIOSBridge();
    const hasWebSocket = this.ws && this.ws.readyState === WebSocket.OPEN;
    
    // On iOS native, use native bridge (discovery happens locally on device)
    // On web, use WebSocket to server
    if (!iosBridge && !hasWebSocket) {
      throw new Error('Not connected to server');
    }
    
    return new Promise((resolve, reject) => {
      const messageId = `discovery_${Date.now()}_${Math.random().toString(36).substring(7)}`;
      
      // Set up timeout (longer for native discovery which can take time)
      const timeout = setTimeout(() => {
        this.pendingCommands.delete(messageId);
        console.warn(`[ClientDiscoveryHandler] Command ${cmd} timed out`);
        reject(new Error(`Command ${cmd} timed out`));
      }, 15000); // 15 seconds for discovery
      
      // Store pending promise
      this.pendingCommands.set(messageId, { resolve, reject, timeout });
      
      // Build BATON message envelope
      const message = {
        v: 1,
        id: messageId,
        ts: Date.now(),
        type: 'command',
        handler: 'discovery',
        location: 'local',
        payload: { cmd, ...payload },
      };
      
      const json = JSON.stringify(message);
      
      // ROUTING: iOS native bridge takes priority for discovery (local network)
      if (iosBridge) {
        console.log(`[ClientDiscoveryHandler] 📲 Sending to iOS native: ${cmd} (id: ${messageId})`);
        iosBridge.postMessage(json);
      } else if (hasWebSocket) {
        // Fallback to WebSocket (for local dev server or web-only scenarios)
        console.log(`[ClientDiscoveryHandler] 📤 Sent via WebSocket: ${cmd} (id: ${messageId})`);
        this.ws!.send(json);
      }
    });
  }
  
  // ---------------------------------------------------------------------------
  // INTERNAL - Notification
  // ---------------------------------------------------------------------------
  
  private notifyZonesChanged(zones: Zone[]): void {
    this.onZonesChangedCallbacks.forEach(cb => {
      try {
        cb(zones);
      } catch (e) {
        console.error('[ClientDiscoveryHandler] Callback error:', e);
      }
    });
  }
  
  private notifyActiveZoneChanged(zone: Zone | null): void {
    this.onActiveZoneChangedCallbacks.forEach(cb => {
      try {
        cb(zone);
      } catch (e) {
        console.error('[ClientDiscoveryHandler] Callback error:', e);
      }
    });
  }
  
  // ---------------------------------------------------------------------------
  // BATON HANDLER INTERFACE
  // ---------------------------------------------------------------------------
  
  canHandle(messageType: string): boolean {
    return messageType.startsWith('discovery:');
  }
  
  getInfo() {
    return {
      handlerId: this.handlerId,
      location: this.location,
      commands: [
        'discovery:startScan',
        'discovery:stopScan',
        'discovery:getZones',
        'discovery:setActiveZone',
        'discovery:getActiveZone',
        'discovery:refreshZones',
      ],
      events: [
        'discovery:zonesChanged',
        'discovery:activeZoneChanged',
        'discovery:scanComplete',
      ],
    };
  }
}

// =============================================================================
// SINGLETON EXPORT — created eagerly so __TAURI__ polling starts immediately
// =============================================================================

const clientDiscoveryHandlerInstance = new ClientDiscoveryHandler();

export function getClientDiscoveryHandler(): ClientDiscoveryHandler {
  return clientDiscoveryHandlerInstance;
}

